import React, { useCallback, useState, type ChangeEvent, useRef } from 'react';
import { styled } from '@compiled/react';
import { Field, ErrorMessage, HelperMessage } from '@atlaskit/form';
import Select from '@atlaskit/select';
import TextField from '@atlaskit/textfield';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { FormattedI18nMessage } from '@atlassian/jira-formatted-i18n-message';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { SIMPLE_DATE_FORMAT_LINK } from '../../../common/constants';
import { LOCALES, TIMEZONES } from '../../../common/constants/date';
import type { SelectOption } from '../../../common/types';
import { Accordion } from '../../../common/ui/accordion';
import { useProjectSpecificContent } from '../../../common/utils/project-specific-content';
import { useProjectData, useAdvancedOptions } from '../../../controllers/import-state';
import { useSource } from '../../../controllers/import-state/selectors';
import messages from './messages';

export const MappingAdvancedOptions = () => {
	const dateTimeRef = useRef<HTMLElement>(null);
	const sendDateFormatUpdateEvent = useRef(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { formatMessage } = useIntl();
	const [advancedOptions, { updateAdvancedOptions, removeDateValidationErrors }] =
		useAdvancedOptions();
	const [hasDateTimeFormatError, setHasDateTimeFormatError] = useState(false);

	const validateDateTimeFormat = useCallback((value?: string) => {
		if (!value || value.trim().length === 0) {
			return setHasDateTimeFormatError(true);
		}
		return setHasDateTimeFormatError(false);
	}, []);

	const [{ type }] = useProjectData();
	const [source] = useSource();
	const {
		mappingAdvancedOptionsHeading,
		mappingAdvancedOptionsPopupHeading,
		mappingAdvancedOptionsPopupDescription,
	} = useProjectSpecificContent(type, source);

	const onDateFormatChange = (event: ChangeEvent<HTMLInputElement>) => {
		validateDateTimeFormat(event.target.value);
		updateAdvancedOptions({ dateFormat: event.target.value.trim() });
		removeDateValidationErrors();
		if (!sendDateFormatUpdateEvent.current) {
			fireUIAnalytics(createAnalyticsEvent({}), 'fieldMappingDateTimeFormat updated');
			sendDateFormatUpdateEvent.current = true;
		}
	};

	const onTimezoneChange = useCallback(
		(timeZoneOption: SelectOption | null) => {
			if (!timeZoneOption) return;

			updateAdvancedOptions({ timeZoneOption });
			removeDateValidationErrors();
		},
		[removeDateValidationErrors, updateAdvancedOptions],
	);

	const onLocaleChange = useCallback(
		(localeOption: SelectOption | null) => {
			if (!localeOption) return;

			updateAdvancedOptions({ localeOption });
			removeDateValidationErrors();
		},
		[removeDateValidationErrors, updateAdvancedOptions],
	);

	const onAccordionClick = useCallback(() => {
		dateTimeRef.current?.focus();
	}, []);

	return (
		<Accordion
			testId="project-create-importer-upload-flow.ui.mapping-screen.mapping-advanced-options.accordion"
			{...(hasDateTimeFormatError ? { onCloseClick: onAccordionClick } : {})}
			heading={mappingAdvancedOptionsHeading}
			infoHeading={formatMessage(mappingAdvancedOptionsPopupHeading)}
			infoDescription={
				<FormattedI18nMessage
					message={formatMessage(mappingAdvancedOptionsPopupDescription, {
						linkStart: '{linkStart}',
						linkEnd: '{linkEnd}',
					})}
					componentsMapping={{
						link: ({ children }) => (
							<Link href={SIMPLE_DATE_FORMAT_LINK} target="_blank">
								{children}
							</Link>
						),
					}}
				/>
			}
		>
			<ContentWrapper>
				<Field
					label={formatMessage(messages.dateAndTimeFormat)}
					name="dateAndTimeFormat"
					isRequired
				>
					{({ fieldProps }) => (
						<>
							<TextField
								{...fieldProps}
								ref={dateTimeRef}
								isRequired
								onChange={onDateFormatChange}
								isInvalid={hasDateTimeFormatError}
								testId="project-create-importer-upload-flow.ui.mapping-screen.mapping-advanced-options.date-time-format-text-field"
								defaultValue={advancedOptions.dateFormat}
							/>
							<HelperMessage>{formatMessage(messages.dateFormatHelperMessage)}</HelperMessage>
							{hasDateTimeFormatError && (
								<ErrorMessage testId="project-create-importer-upload-flow.ui.mapping-screen.mapping-advanced-options.error-message">
									{formatMessage(messages.dateFormatEmpty)}
								</ErrorMessage>
							)}
						</>
					)}
				</Field>
				<Field label={formatMessage(messages.locale)} name="locale">
					{({ fieldProps }) => (
						<Select
							{...fieldProps}
							value={advancedOptions.localeOption}
							options={LOCALES}
							onChange={onLocaleChange}
							menuPlacement="auto"
						/>
					)}
				</Field>
				<Field label={formatMessage(messages.timezone)} name="timezone">
					{({ fieldProps }) => (
						<Select
							{...fieldProps}
							value={advancedOptions.timeZoneOption}
							options={TIMEZONES}
							onChange={onTimezoneChange}
							menuPlacement="auto"
						/>
					)}
				</Field>
			</ContentWrapper>
		</Accordion>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		margin: `0 ${token('space.400', '16px')} ${token('space.200', '16px')} 0`,
		width: '200px',
		maxWidth: '200px',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Link = styled.a({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.accent.blue', colors.B400),
});
